import { comuneLabel } from '@sse/gazetteer';
import type { Event } from '@sse/schema';
import { ArrowLeft, ArrowLeftRight, ExternalLink, Merge, Search } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  Cell,
  ColumnHead,
  DatesView,
  FIELD_LABEL,
  LinksView,
  PlaceView,
} from '../components/review-panel';
import { StatusBadge, btnGhost, btnPrimary, btnSecondary, inputCls } from '../components/ui';
import {
  ApiError,
  MERGE_FIELDS,
  type AdminEventRow,
  type EventMergePreview,
  type MergeField,
} from '../lib/api';
import { categoryLabel, formatRange } from '../lib/format';
import { useSession } from '../lib/session';

/** `target` resta (id, indirizzo, storico); `source` si unisce e sparisce */
type Pos = 'target' | 'source';
/** nell'anteprima dell'API: `event` = target, `candidate` = source */
const apiSide = (p: Pos) => (p === 'target' ? 'event' : 'candidate');

const texts = (e: Event) =>
  e.shortDesc || e.description ? (
    <>
      {e.shortDesc && <div>{e.shortDesc}</div>}
      {e.description && <div className="mt-1 line-clamp-4 text-xs text-muted">{e.description}</div>}
    </>
  ) : (
    <span className="text-muted">—</span>
  );

const cellOf = (e: Event): Record<MergeField, ReactNode> => ({
  title: e.title,
  dates: <DatesView dates={e.dates} />,
  place: (
    <PlaceView
      p={{
        comuneId: e.comuneId,
        ...(e.frazione ? { frazione: e.frazione } : {}),
        ...(e.venue ? { venue: e.venue } : {}),
      }}
    />
  ),
  category: categoryLabel(e.category),
  texts: texts(e),
  links: <LinksView links={e.links ?? []} />,
});

/**
 * Unione di due eventi (docs/07-admin-e-bot.md, "Doppioni"). Le due schede affiancate campo per
 * campo, come nella coda di revisione: si sceglie quale evento resta (indirizzo, statistiche,
 * storico) e da quale versione prendere ogni campo. L'altro evento sparisce e il suo indirizzo
 * porta a quello che resta.
 */
export function EventMerge() {
  const { api, config } = useSession();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const target = sp.get('target') ?? '';
  const source = sp.get('source') ?? '';
  const [keep, setKeep] = useState<Pos>('target');
  const [picks, setPicks] = useState<Partial<Record<MergeField, Pos>>>({});
  const [preview, setPreview] = useState<EventMergePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target || !source) return;
    let stale = false;
    setError(null);
    api<EventMergePreview>('/events/merge/preview', {
      method: 'POST',
      body: { targetId: target, sourceId: source, keep, picks },
    })
      .then((p) => !stale && setPreview(p))
      .catch(
        (e: unknown) =>
          !stale &&
          setError(
            e instanceof ApiError
              ? e.status === 404
                ? 'Uno dei due eventi non esiste più.'
                : e.message
              : String(e),
          ),
      );
    return () => {
      stale = true;
    };
  }, [api, target, source, keep, picks]);

  const swap = () => {
    setPreview(null);
    setKeep('target');
    setPicks({});
    setSp({ target: source, source: target }, { replace: true });
  };

  const merge = async () => {
    if (!preview) return;
    const { target: t, source: s } = preview;
    if (
      !window.confirm(
        `Unire questi due eventi?\n\nResta: «${t.title}» (/events/${t.slug})\nViene eliminato: «${s.title}» (/events/${s.slug}), il suo indirizzo porterà all’altro.\n\nNon si può annullare.`,
      )
    )
      return;
    setBusy(true);
    try {
      await api('/events/merge', {
        method: 'POST',
        body: {
          targetId: t.id,
          sourceId: s.id,
          keep,
          picks,
          targetVersion: t.version,
          sourceVersion: s.version,
        },
      });
      navigate(`/events/${t.id}`, { replace: true, state: { merged: s.title } });
    } catch (e) {
      setBusy(false);
      setError(
        e instanceof ApiError && e.body.error === 'version_conflict'
          ? 'Uno dei due eventi è cambiato nel frattempo: ricarica la pagina e riprova.'
          : `Unione non riuscita: ${String(e)}`,
      );
    }
  };

  if (!target) return <Navigate404 />;
  if (!source)
    return (
      <Shell>
        <PickOther targetId={target} onPick={(id) => setSp({ target, source: id })} />
      </Shell>
    );

  const t = preview?.target;
  const s = preview?.source;
  const cells = t && s ? { target: cellOf(t), source: cellOf(s) } : null;
  const head = (e: Event | undefined, pos: Pos) => (
    <ColumnHead
      title={
        e ? (
          <Link to={`/events/${e.id}`} target="_blank" className="hover:underline">
            {pos === 'target' ? 'Resta' : 'Viene unito ed eliminato'}
          </Link>
        ) : (
          '…'
        )
      }
      extra={
        e && (
          <>
            <StatusBadge status={e.status} />
            {e.status !== 'draft' && (
              <a
                href={`${config.siteUrl}/events/${e.slug}`}
                target="_blank"
                rel="noopener"
                title="Apri sul sito"
              >
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
            )}
          </>
        )
      }
      selectable={Boolean(preview)}
      selected={keep === pos}
      onSelect={() => {
        setKeep(pos);
        setPicks({});
      }}
    />
  );

  return (
    <Shell>
      <p className="text-sm text-muted">
        Resta l’evento a sinistra: id, indirizzo, statistiche e storico. Scegli la versione dei
        campi dall’intestazione della colonna e clicca una cella per prendere quel campo dall’altra.
        In verde quello che resterà. I campi vuoti si completano da soli.
      </p>
      <div className="flex flex-wrap gap-2">
        <button onClick={swap} className={btnSecondary} disabled={busy}>
          <ArrowLeftRight className="size-4" aria-hidden /> Fai restare l’altro
        </button>
      </div>

      {error && (
        <p role="alert" className="bg-danger-soft p-3 text-sm">
          {error}
        </p>
      )}

      <table className="w-full table-fixed border-collapse text-sm">
        <thead>
          <tr className="border-b-2 border-fg text-left align-bottom">
            <th className="w-28 py-2 pr-2" />
            <th className="py-2 pr-3 pl-2 font-normal">{head(t, 'target')}</th>
            <th className="py-2 pl-2 font-normal">{head(s, 'source')}</th>
          </tr>
        </thead>
        <tbody>
          {MERGE_FIELDS.map((f) => {
            const o = preview?.origin[f];
            const avail = preview?.available[f];
            const taken = (p: Pos) => o === apiSide(p) || (o === 'filled' && p === 'target');
            return (
              <tr key={f} className="border-b border-surface-strong align-top">
                <th className="py-2 pr-2 text-left text-xs font-normal text-muted">
                  {FIELD_LABEL[f]}
                  {picks[f] && picks[f] !== keep && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-fg">
                      <ArrowLeftRight className="size-3" aria-hidden /> spostato
                    </div>
                  )}
                </th>
                {(['target', 'source'] as const).map((p) => (
                  <Cell
                    key={p}
                    strong={f === 'title'}
                    selected={Boolean(o) && taken(p)}
                    filledFrom={o === 'filled' && p === 'target' ? 'dell’altro evento' : undefined}
                    clickable={Boolean(preview) && o !== apiSide(p) && Boolean(avail?.[apiSide(p)])}
                    onPick={() => setPicks((x) => ({ ...x, [f]: p }))}
                  >
                    {cells ? cells[p][f] : <span className="text-muted">…</span>}
                  </Cell>
                ))}
              </tr>
            );
          })}
          {t && s && (
            <>
              <Row label="Immagine">
                <td className="py-2 pr-3 pl-2">{t.image ? 'sì' : '—'}</td>
                <td className="py-2 pl-2">
                  {s.image
                    ? t.image
                      ? 'sì (resta quella a sinistra)'
                      : 'sì: passa all’evento'
                    : '—'}
                </td>
              </Row>
              <Row label="Indirizzo">
                <td className="py-2 pr-3 pl-2 break-all">/events/{t.slug}</td>
                <td className="py-2 pl-2 break-all">
                  /events/{s.slug}
                  <div className="text-xs text-muted">porterà a quello a sinistra</div>
                </td>
              </Row>
              <Row label="Comune">
                <td className="py-2 pr-3 pl-2">{comuneLabel(t.comuneId)}</td>
                <td className="py-2 pl-2">{comuneLabel(s.comuneId)}</td>
              </Row>
            </>
          )}
        </tbody>
      </table>

      {preview && (
        <div className="flex flex-col gap-2 border border-surface-strong bg-surface p-3 text-sm">
          <p>
            <b>Risultato:</b> {preview.event.title} ·{' '}
            {formatRange(preview.event.startDate, preview.event.endDate)} ·{' '}
            {comuneLabel(preview.event.comuneId)}
          </p>
          <ul className="list-disc pl-5 text-xs text-muted">
            <li>«{preview.source.title}» viene eliminato: le sue statistiche non si sommano.</li>
            <li>Le sorgenti che lo leggevano aggiorneranno l’evento che resta.</li>
            <li>I campi presi dall’altro evento restano bloccati, come una modifica a mano.</li>
          </ul>
          <div>
            <button onClick={() => void merge()} disabled={busy} className={btnPrimary}>
              <Merge className="size-4" aria-hidden /> {busy ? 'Unione…' : 'Unisci gli eventi'}
            </button>
          </div>
        </div>
      )}
    </Shell>
  );
}

const Row = ({ label, children }: { label: string; children: ReactNode }) => (
  <tr className="border-b border-surface-strong align-top">
    <th className="py-2 pr-2 text-left text-xs font-normal text-muted">{label}</th>
    {children}
  </tr>
);

function Shell({ children }: { children: ReactNode }) {
  return (
    <main className="mx-auto flex max-w-[1400px] flex-col gap-4 p-4 md:p-6">
      <div>
        <Link to="/events/duplicates" className={btnGhost}>
          <ArrowLeft className="size-4" aria-hidden /> Doppioni
        </Link>
        <h1 className="mt-2 font-display text-h2">Unisci due eventi</h1>
      </div>
      {children}
    </main>
  );
}

function Navigate404() {
  return (
    <Shell>
      <p className="text-sm">
        Scegli gli eventi da unire dai <Link to="/events/duplicates">doppioni</Link> o dalla scheda
        di un evento.
      </p>
    </Shell>
  );
}

/** Scelta dell'altro evento (dalla scheda: "Unisci con un altro evento"). */
function PickOther({ targetId, onPick }: { targetId: string; onPick: (id: string) => void }) {
  const { api } = useSession();
  const [base, setBase] = useState<Event | null>(null);
  const [text, setText] = useState('');
  const [sameComune, setSameComune] = useState(true);
  const [rows, setRows] = useState<AdminEventRow[] | null>(null);

  useEffect(() => {
    api<{ event: Event }>(`/events/${targetId}`).then(
      (d) => setBase(d.event),
      () => undefined,
    );
  }, [api, targetId]);

  // nello stesso comune anche senza testo (gli eventi in programma); altrove serve la ricerca
  const q = text.trim();
  useEffect(() => {
    if (!base || (!sameComune && q.length < 3)) return;
    const t = setTimeout(() => {
      const p = new URLSearchParams({ when: q ? 'all' : 'upcoming', limit: '50' });
      if (q) p.set('q', q);
      if (sameComune) p.set('comune', base.comuneId);
      const run = (status: string) =>
        api<{ items: AdminEventRow[] }>(`/events?${p}&status=${status}`).then((r) => r.items);
      void Promise.all(['published', 'draft', 'cancelled'].map(run)).then((lists) =>
        setRows(lists.flat().filter((r) => r.id !== targetId)),
      );
    }, 400);
    return () => clearTimeout(t);
  }, [api, base, q, sameComune, targetId]);

  // prima quelli con date vicine
  const sorted = useMemo(() => {
    if (!rows || !base) return rows;
    const dist = (r: AdminEventRow) =>
      Math.abs(Date.parse(r.startDate) - Date.parse(base.startDate));
    return [...rows].sort((a, b) => dist(a) - dist(b));
  }, [rows, base]);

  return (
    <>
      <p className="text-sm">
        Evento che resta: <b>{base?.title ?? '…'}</b>
        {base && ` · ${formatRange(base.startDate, base.endDate)} · ${comuneLabel(base.comuneId)}`}
      </p>
      <label className="relative max-w-xl">
        <span className="sr-only">Cerca l’evento da unire</span>
        <Search className="absolute top-3 left-2 size-4 text-muted" aria-hidden />
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Cerca l’evento da unire per titolo, comune o slug…"
          className={`${inputCls} pl-8`}
        />
      </label>
      {base && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sameComune}
            onChange={(e) => setSameComune(e.target.checked)}
          />
          Solo a {comuneLabel(base.comuneId)}
        </label>
      )}
      {sorted && sorted.length === 0 && (
        <p className="text-sm text-muted">Nessun evento trovato.</p>
      )}
      {sorted && sorted.length > 0 && (
        <ul className="flex flex-col divide-y divide-surface-strong border border-surface-strong">
          {sorted.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => onPick(r.id)}
                className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-surface"
              >
                <span className="font-medium">{r.title}</span>
                <StatusBadge status={r.status} />
                <span className="text-xs text-muted">
                  {formatRange(r.startDate, r.endDate)} ·{' '}
                  {[r.frazione, comuneLabel(r.comuneId) || r.comune].filter(Boolean).join(', ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
